import {
  Component,
  ComponentRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  Renderer2,
  Type,
  ViewContainerRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Column, GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';

import { buildTableCellParams, isAngularComponent } from '../../../helpers/table-params';

/**
 * Cellule de la vue tableau (`cam-grid-table`).
 *
 * Rend le contenu exactement comme AG Grid le ferait pour la colonne : le `cellRenderer` de la
 * `ColDef` (fixé par le type de colonne — `GridDateCellComponent`, `GridCurrencyCellComponent`… — ou
 * surchargé via `fieldsOverrides`) reçoit les mêmes `params` que dans la grille. Trois formes :
 * - composant Angular (`ICellRendererAngularComp`) → instancié dans l'outlet, `agInit(params)` ;
 * - fonction `(params) => string | HTMLElement` → HTML injecté tel quel (même confiance qu'AG Grid) ;
 * - aucun renderer → `valueFormatted` puis `value` en texte.
 */
@Component({
  host: { class: 'grid-table-cell' },
  selector: 'cam-grid-table-cell',
  styleUrl: './grid-table-cell.component.scss',
  templateUrl: './grid-table-cell.component.html',
})
export class GridTableCellComponent<T> implements OnChanges, OnDestroy {
  api = input.required<GridApi<T>>();
  column = input.required<Column>();
  node = input.required<IRowNode<T>>();
  /** Incrémenté par la table à chaque mise à jour du modèle AG Grid : force le re-rendu du contenu. */
  version = input(0);

  public readonly html = signal<SafeHtml | null>(null);
  public readonly text = signal<string | null>(null);

  private readonly _outlet = viewChild.required('outlet', { read: ViewContainerRef });
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _sanitizer = inject(DomSanitizer);

  private _componentRef: ComponentRef<ICellRendererAngularComp> | null = null;
  private _element: HTMLElement | null = null;

  ngOnChanges(): void {
    this._render();
  }

  ngOnDestroy(): void {
    this._clear();
  }

  private _render(): void {
    this._clear();

    const node = this.node();
    if (!node.data) {
      return;
    }
    const api = this.api();
    const column = this.column();
    const params = buildTableCellParams(api, column, node, this._host.nativeElement);
    const cellRenderer = this._resolveRenderer(column);

    if (isAngularComponent(cellRenderer)) {
      this._componentRef = this._outlet().createComponent(cellRenderer as Type<ICellRendererAngularComp>);
      this._componentRef.instance.agInit(params);
      return;
    }
    if (typeof cellRenderer === 'function') {
      this._applyRendererResult((cellRenderer as (params: ICellRendererParams) => unknown)(params));
      return;
    }
    this.text.set(params.valueFormatted ?? this._stringify(params.value));
  }

  /**
   * Un renderer enregistré par nom (ex. `agGroupCellRenderer` de la colonne auto-group en tree data) n'est
   * pas instanciable hors AG Grid : on rend alors son `innerRenderer` (`ITreeData.autoGroupColumnDef`),
   * le chevron d'expansion étant déjà porté par la première cellule de `cam-grid-table`.
   */
  private _resolveRenderer(column: Column): unknown {
    const colDef = column.getColDef();
    if (typeof colDef.cellRenderer !== 'string') {
      return colDef.cellRenderer;
    }
    const rendererParams = colDef.cellRendererParams as { innerRenderer?: unknown } | undefined;
    return rendererParams?.innerRenderer ?? null;
  }

  private _applyRendererResult(result: unknown): void {
    if (typeof result === 'string') {
      this.html.set(this._sanitizer.bypassSecurityTrustHtml(result));
      return;
    }
    if (result instanceof HTMLElement) {
      this._element = result;
      this._renderer.appendChild(this._host.nativeElement, result);
    }
  }

  private _clear(): void {
    this._componentRef?.destroy();
    this._componentRef = null;
    if (this._element) {
      this._renderer.removeChild(this._host.nativeElement, this._element);
      this._element = null;
    }
    this.html.set(null);
    this.text.set(null);
  }

  private _stringify(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
  }
}
